import React from 'react';
import moment from 'moment';
import { DatePicker as AntdDatePicker } from 'antd';
import type { DatePickerProps } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import { RangeValue } from 'rc-picker/lib/interface';
import 'moment/locale/fr-ca';
import frLocale from 'antd/es/date-picker/locale/fr_CA';
import enLocale from 'antd/es/date-picker/locale/en_US';

import { FormInstance } from 'antd/es/form/Form';
import { ILocale } from '../../types/ILocale';
import useLocale from '../../hooks/useLocale';

import './DatePicker-KS.scss';
import './DatePicker.scss';

export type ValueSingle = moment.Moment | null;
export type ValueRange = RangeValue<moment.Moment | null> | [];

const { RangePicker: AntdRangePicker } = AntdDatePicker;

/* =======================
   Shared types
======================= */

export type PickerTypes = 'single' | 'range';

export type DateSetter = React.Dispatch<React.SetStateAction<moment.Moment | null>>;
export type DateRangeSetter = React.Dispatch<React.SetStateAction<RangeValue<moment.Moment | null> | []>>;

interface ICommonProps {
  fullWidth?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

interface IFormProps {
  form: FormInstance<any>;
  formFieldName: string;
}

interface IStandardProps {
  form?: never;
  formFieldName?: never;
}

/* =======================
   SINGLE date picker props
======================= */

type SingleDatePickerProps = {
  type?: 'single';
  dateSetter?: DateSetter | null;
  errorMsgSetter?: React.Dispatch<React.SetStateAction<string>>;
} & DatePickerProps &
  (IFormProps | IStandardProps) &
  ICommonProps;

/* =======================
   RANGE date picker props
======================= */

type RangeDatePickerProps = {
  type: 'range';
  dateSetter?: DateRangeSetter | null;
  errorMsgSetter?: React.Dispatch<React.SetStateAction<string>>;
} & RangePickerProps &
  (IFormProps | IStandardProps) &
  ICommonProps;

type IDatePickerProps = SingleDatePickerProps | RangeDatePickerProps;

/* =======================
   Helper
======================= */

function getClearedRange(
  current: RangeValue<moment.Moment | null> | [],
  placeholder: string
): RangeValue<moment.Moment | null> | [] {
  const start = current?.[0] ?? null;
  const end = current?.[1] ?? null;

  if (placeholder === 'Start date' && end) return [null, end];
  if (placeholder === 'End date' && start) return [start, null];
  return [];
}

/* =======================
   Component
======================= */

function DatePicker(props: IDatePickerProps): JSX.Element {
  const { locale } = useLocale();

  const { type = 'single', fullWidth, className, style, errorMsgSetter } = props;

  /* =======================
       SINGLE DATE PICKER
    ======================= */

  if (type === 'single') {
    const { form, formFieldName, dateSetter, ...antdProps } = props as SingleDatePickerProps;

    const onChange: DatePickerProps['onChange'] =
      antdProps.onChange ??
      (!form && dateSetter
        ? (value) => {
            dateSetter(value);
            errorMsgSetter?.('');
          }
        : undefined);

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.code === 'Enter' && e.currentTarget.value === '') {
        dateSetter?.(null);
        form && formFieldName && form.setFieldsValue({ [formFieldName]: null });
      }
    };

    return (
      <div className={`DatePicker_Custom ${fullWidth ? 'full-width' : ''} ${className ?? ''}`} style={style}>
        <AntdDatePicker
          picker="date"
          {...antdProps}
          onChange={onChange}
          onKeyDown={onKeyDown}
          dropdownClassName="DatePicker_Custom_Dropdown"
          locale={locale === ILocale.fr ? frLocale : enLocale}
        />
      </div>
    );
  }

  /* =======================
       RANGE DATE PICKER
    ======================= */

  const { form, formFieldName, dateSetter, ...rangeProps } = props as RangeDatePickerProps;

  const onChange: RangePickerProps['onChange'] =
    rangeProps.onChange ??
    (!form && dateSetter
      ? (values) => {
          dateSetter(values ?? []);
          errorMsgSetter?.('');
        }
      : undefined);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.code === 'Enter' && e.currentTarget.value === '') {
      const placeholder = e.currentTarget.placeholder;

      if (dateSetter) {
        dateSetter((prev) => getClearedRange(prev, placeholder));
      }

      if (form && formFieldName) {
        const current = form.getFieldValue(formFieldName);
        form.setFieldsValue({
          [formFieldName]: getClearedRange(current, placeholder),
        });
      }
    }
  };

  return (
    <div className={`DatePicker_Custom ${fullWidth ? 'full-width' : ''} ${className ?? ''}`} style={style}>
      <AntdRangePicker
        {...rangeProps}
        onChange={onChange}
        onKeyDown={onKeyDown}
        dropdownClassName="DatePicker_Custom_Dropdown"
        locale={locale === ILocale.fr ? frLocale : enLocale}
      />
    </div>
  );
}

export default DatePicker;
